import React, { useEffect, useState } from 'react';
import {
  Paper,
  IconButton,
  Button,
  Grid,
  FormControl,
  TextField,
  Autocomplete,
  createFilterOptions,
  Box,
  Chip,
} from '@mui/material';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import Message from '../../tools/Message';
import Title from '../../../components/Title';
import { makeStyles } from '@material-ui/core';
import TimeSheetMissionService from '../../../services/TimeSheetMissionService';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import MainContainer from '../../../components/MainContainer';
import SearchIcon from '@mui/icons-material/Search';
import Spacer from '../../../components/Spacer';
import ProvisionalFuelMissionService from '../../../services/ProvisionalFuelMissionService';
import { headerDataImmo } from '../../../data/modules/logistic-header';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    elevation: 2,
    borderRadius: '8px',
    marginTop: '1%',
    height: '60%',
    boxShadow:
      'box-shadow: rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;',
  },
  btn: {
    margin: '1% 0.25% 1% 0.25%',
    fontWeight: 600,
  },
}));

const formatFilterDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const createDefaultAccountingMissionFilter = () => {
  const now = new Date();
  const firstDayCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayThirdMonth = new Date(now.getFullYear(), now.getMonth() + 4, 0);

  return {
    title: '',
    state: ['PENDING', 'CONFIRMED'],
    reference: '',
    destination: '',
    debut: formatFilterDate(firstDayCurrentMonth),
    fin: formatFilterDate(lastDayThirdMonth),
  };
};

const formatDate = (value) => {
  if (!value) return '—';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('fr-FR');
};

const renderWrappedCell = (params) => (
  <Box
    sx={{
      width: '100%',
      py: 1.25,
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      lineHeight: 1.4,
    }}
  >
    {params.value || '—'}
  </Box>
);

const AccountingMissionConfirmedList = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [rows, setRows] = useState([]);
  const [reference, setReference] = useState([]);
  const [filter, setFilter] = useState(createDefaultAccountingMissionFilter);
  const [appliedFilter, setAppliedFilter] = useState(createDefaultAccountingMissionFilter);
  const [message, setMessage] = useState({
    open: false,
    color: 'green',
    messageInfo: '',
  });
  const { open, color, messageInfo } = message;
  const STATE_FUEL = [
    { label: 'Nouveau', value: 'NEW', color: '#33ccff' },
    { label: 'En attente de confirmation', value: 'PENDING', color: 'purple' },
    { label: 'Confirmé', value: 'CONFIRMED', color: 'green' },
    { label: 'Refusé', value: 'REFUSED', color: 'red' },
  ];

  const referenceFilterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option ?? '',
  });

  function handleCloseMessage() {
    setMessage({ ...message, open: false });
  }

  const handleChangeFilter = (event) => {
    const { name, value } = event.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilter = () => {
    setAppliedFilter({ ...filter, state: ['PENDING', 'CONFIRMED'] });
  };

  const handleResetFilter = () => {
    const defaultFilter = createDefaultAccountingMissionFilter();
    setFilter({ ...defaultFilter });
    setAppliedFilter({ ...defaultFilter });
  };

  const fetchData = async (currentFilter = appliedFilter) => {
    setOpenLoadingSimpleBackdrop(true);
    try {
      const response = await ProvisionalFuelMissionService.getProvisionalFuelMissionAccount({
        ...currentFilter,
        state: ['PENDING', 'CONFIRMED'],
      });
      if (response && response.status) {
        const data = Array.isArray(response.data) ? response.data : [];
        setRows(
          data.map((item, index) => ({
            id: item.provisional_fuel_id || `${item.mission_planning_id}-${item.mission_planning_agenda_id}-${index}`,
            ...item,
          }))
        );
        const references = [...new Set(data.map((item) => item.reference).filter(Boolean))];
        setReference(references);
      } else {
        setRows([]);
      }
    } catch (error) {
      setRows([]);
      setMessage({
        open: true,
        color: 'red',
        messageInfo: 'Erreur lors du chargement des donn\u00E9es.',
      });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  useEffect(() => {
    fetchData(appliedFilter);
  }, [appliedFilter]);

  const handleDownloadFile = async (mission_planning_id, mission_planning_agenda_id, mission_timesheet_id) => {
    setOpenLoadingSimpleBackdrop(true);
    try {
      const response = await TimeSheetMissionService.downloadTimesheetFile(
        mission_planning_id,
        mission_planning_agenda_id,
        mission_timesheet_id
      );
      if (response) {
        const fileURL = window.URL.createObjectURL(new Blob([response], { type: 'application/zip' }));
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', `timesheet_${mission_timesheet_id}.zip`);
        document.body.appendChild(fileLink);
        fileLink.click();
        fileLink.remove();
        window.URL.revokeObjectURL(fileURL);
      }
    } catch (error) {
      setMessage({
        open: true,
        color: 'red',
        messageInfo: 'Erreur lors du téléchargement du fichier.',
      });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  const navigateToFuelConfirmed = (row) => {
    navigate(
      `/accounting/fuel_confirmed/${row.mission_planning_id}/${row.mission_planning_agenda_id}/${row.mission_timesheet_id}/${row.provisional_fuel_id}`
    );
  };

  const columns = [
    {
      field: 'reference',
      headerName: 'Référence',
      width: 140,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <Box sx={{ fontWeight: 600 }}>{params.value || '—'}</Box>,
    },
    {
      field: 'mission_title',
      headerName: 'Objet de la mission',
      flex: 1.3,
      minWidth: 280,
      renderCell: renderWrappedCell,
    },
    {
      field: 'lieu',
      headerName: 'Destination',
      flex: 0.9,
      minWidth: 200,
      renderCell: renderWrappedCell,
    },
    {
      field: 'missionnaries',
      headerName: 'Missionnaires',
      flex: 1,
      minWidth: 240,
      renderCell: (params) => {
        const missionnaries = params.row.missionnaries || [];
        return (
          <Box sx={{ width: '100%', py: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {missionnaries.length > 0 ? missionnaries.map((missionary, index) => (
              <Box
                key={missionary.user_id || `${missionary.name}-${index}`}
                sx={{ display: 'flex', alignItems: 'flex-start', lineHeight: 1.35 }}
              >
                <Box component="span" sx={{ color: 'primary.main', mr: 0.75, fontWeight: 700 }}>
                  •
                </Box>
                <Box component="span" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                  {missionary.name || '—'}
                </Box>
              </Box>
            )) : '—'}
          </Box>
        );
      },
    },
    {
      field: 'date_begin',
      headerName: 'Date début',
      width: 125,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'date_end',
      headerName: 'Date fin',
      width: 125,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'provisional_fuel_state',
      headerName: 'Statut Carburant',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const state = STATE_FUEL.find((option) => option.value === params.row.provisional_fuel_state);
        return (
          <Chip
            label={state?.label || 'Nouveau'}
            size="small"
            variant="outlined"
            sx={{
              maxWidth: '100%',
              color: state?.color || '#33ccff',
              borderColor: state?.color || '#33ccff',
              fontWeight: 600,
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        );
      },
    },
    {
      field: 'indemnity_validation_state',
      headerName: 'Statut Indemnité',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const state = STATE_FUEL.find((option) => option.value === params.row.indemnity_validation_state);
        return (
          <Chip
            label={state?.label || 'Nouveau'}
            size="small"
            variant="outlined"
            sx={{
              maxWidth: '100%',
              color: state?.color || '#33ccff',
              borderColor: state?.color || '#33ccff',
              fontWeight: 600,
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        );
      },
    },
    {
      field: 'action',
      headerName: 'Actions',
      width: 130,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        params.row.provisional_fuel_state === 'CONFIRMED' &&
        params.row.indemnity_validation_state === 'CONFIRMED' ? (
          <IconButton
            color="info"
            aria-label="voir la confirmation carburant et indemnité"
            size="small"
            onClick={() => navigateToFuelConfirmed(params.row)}
          >
            <RemoveRedEyeIcon />
          </IconButton>
        ) : (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => navigateToFuelConfirmed(params.row)}
          >
            Valider
          </Button>
        )
      ),
    },
  ];

  return (
    <MainContainer {...headerDataImmo}>
      <>
        <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />
        <Message open={open} color={color} messageInfo={messageInfo} handleClose={handleCloseMessage} />
        <Grid container xs={12} spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <label>Objet de la mission :</label>
            <FormControl fullWidth variant="outlined" size="small">
              <TextField fullWidth id="title" size="small" variant="outlined" value={filter.title} name="title" onChange={handleChangeFilter} />
            </FormControl>
          </Grid>
          <Grid item container xs={12} sm={6} md={6} spacing={4} justifyContent={'flex-end'}>
            <Spacer x={1} />
            <div style={{ paddingTop: '50px' }}>
              <Button variant="outlined" color="secondary" onClick={handleResetFilter} className={classes.btn}>
                {'R\u00E9initialiser'}
              </Button>
            </div>
            <Spacer x={1} />
            <div style={{ paddingTop: '50px' }}>
              <Button variant="contained" color="primary" onClick={handleFilter} className={classes.btn}>
                <SearchIcon style={{ marginRight: '8px' }} />
                Filtrer
              </Button>
            </div>
            <Spacer x={1} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <label>{'R\u00E9f\u00E9rence :'}</label>
            <FormControl fullWidth variant="outlined" size="small">
              <Autocomplete
                id="reference-filter"
                freeSolo
                options={reference}
                filterOptions={referenceFilterOptions}
                sx={{
                  '& .MuiInputBase-root': { height: 40 },
                  '& .MuiInputBase-input': { padding: '4px 8px' },
                }}
                getOptionLabel={(option) => `${option ?? ''}`}
                filterSelectedOptions
                onChange={(_, newValue) => {
                  setFilter((prev) => ({ ...prev, reference: newValue || '' }));
                }}
                inputValue={filter.reference}
                onInputChange={(_, newInputValue) => {
                  setFilter((prev) => ({ ...prev, reference: newInputValue || '' }));
                }}
                size="small"
                value={filter.reference}
                renderInput={(params) => <TextField {...params} variant="outlined" size="small" />}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            Destination:
            <TextField fullWidth id="destination" size="small" variant="outlined" value={filter.destination} name="destination" onChange={handleChangeFilter} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {'Date d\u00E9but:'}
            <TextField fullWidth id="date_debut" size="small" variant="outlined" type="date" value={filter.debut} name="debut" onChange={handleChangeFilter} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            Date fin:
            <TextField fullWidth id="date_fin" size="small" variant="outlined" type="date" value={filter.fin} name="fin" onChange={handleChangeFilter} />
          </Grid>
        </Grid>
        <Paper className={classes.paper} variant="outlined">
          <Title>{'Carburants / Indemnités prévisionnels'}</Title>
          <div style={{ height: 640, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowHeight={() => 'auto'}
              getEstimatedRowHeight={() => 72}
              pageSize={10}
              rowsPerPageOptions={[10]}
              disableSelectionOnClick
              getRowId={(row) => row.id}
              sx={{
                border: 0,
                color: '#334155',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f4f7fb',
                  borderBottom: '1px solid #dce6f3',
                  color: '#29476f',
                  fontWeight: 700,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  whiteSpace: 'normal',
                  lineHeight: 1.25,
                },
                '& .MuiDataGrid-row': {
                  minHeight: '56px !important',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f8fbff',
                },
                '& .MuiDataGrid-cell': {
                  alignItems: 'center',
                  display: 'flex',
                  minHeight: '56px !important',
                  py: 0.5,
                  whiteSpace: 'normal',
                  lineHeight: 1.4,
                  borderColor: '#edf1f7',
                  overflow: 'visible',
                },
                '& .MuiDataGrid-cellContent': {
                  width: '100%',
                  whiteSpace: 'normal',
                  lineHeight: 1.4,
                  overflowWrap: 'anywhere',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #dce6f3',
                },
              }}
            />
          </div>
        </Paper>
      </>
    </MainContainer>
  );
};

export default AccountingMissionConfirmedList;

